import React, { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Tab, Tabs } from "@mui/material";
import {
  canAccessAdministrationSection,
  canReadModuleOrLegacy,
  isSystemAdmin,
} from "../../utils/appRole";

/** Each tab maps to `accountAccess.administrationSections.<sectionKey>`. */
const TAB_CONFIG = [
  { label: "Camera Management", path: "camera-mgmt", sectionKey: "cameraManagement" },
  { label: "Zone Management", path: "zone-mgmt", sectionKey: "zoneManagement" },
  { label: "AI Management", path: "ai-models-mgmt", sectionKey: "aiModelsManagement" },
  { label: "Storage Management", path: "storage-mgmt", sectionKey: "storageManagement" },
  { label: "User Management", path: "user-mgmt", sectionKey: "userManagement" },
  { label: "IOT Devices Management", path: "iot-devices-mgmt", sectionKey: "iotDevicesManagement" },
  { label: "Patrolling Route Management", path: "patrolling-route-mgmt", sectionKey: "patrollingRouteManagement" },
  { label: "Patrol setting", path: "patrol-settings", sectionKey: "patrolSettings" },
];

function hasAdministrationSections(access) {
  const s = access?.administrationSections;
  return Boolean(s && typeof s === "object" && Object.keys(s).length > 0);
}

function tabVisible(tab, access) {
  if (!access) return false;
  if (isSystemAdmin(access)) return true;

  if (hasAdministrationSections(access)) {
    return canAccessAdministrationSection(access, tab.sectionKey);
  }

  // Legacy fallback when API has no administrationSections yet
  if (tab.sectionKey === "cameraManagement") {
    return canReadModuleOrLegacy(access, "cameras");
  }
  if (tab.sectionKey === "aiModelsManagement") {
    return canReadModuleOrLegacy(access, "ai_models");
  }
  if (tab.sectionKey === "zoneManagement") {
    return canReadModuleOrLegacy(access, "zones");
  }
  if (tab.sectionKey === "userManagement") {
    return (
      canReadModuleOrLegacy(access, "users") || canReadModuleOrLegacy(access, "roles")
    );
  }
  return false;
}

/**
 * Administration area: tabs filtered by GET /api/account/access `administrationSections`.
 */
const AdministrationLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const accountAccess = useSelector((state) => state.auth.accountAccess);
  const accessLoaded = useSelector((state) => state.auth.accessLoaded);

  const visibleTabs = useMemo(() => {
    if (!accessLoaded) return [];
    return TAB_CONFIG.filter((t) => tabVisible(t, accountAccess));
  }, [accessLoaded, accountAccess]);

  const tabValue = useMemo(() => {
    const base = "/administration/";
    if (!location.pathname.startsWith("/administration")) return 0;
    const segment = location.pathname.slice(base.length).split("/")[0] || "";
    const idx = visibleTabs.findIndex((t) => t.path === segment);
    return idx === -1 ? 0 : idx;
  }, [location.pathname, visibleTabs]);

  useEffect(() => {
    if (!accessLoaded || visibleTabs.length === 0) return;
    const base = "/administration/";
    const segment = location.pathname.slice(base.length).split("/")[0] || "";
    const allowed = visibleTabs.some((t) => t.path === segment);
    if (!allowed) {
      navigate(`/administration/${visibleTabs[0].path}`, { replace: true });
    }
  }, [accessLoaded, location.pathname, navigate, visibleTabs]);

  const handleTabChange = (_event, next) => {
    const nextPath = visibleTabs[next]?.path;
    if (nextPath) navigate(`/administration/${nextPath}`);
  };

  if (accessLoaded && visibleTabs.length === 0) {
    return (
      <Box sx={{ px: 2, py: 4 }}>
        <p>You do not have access to any administration sections.</p>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 2, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", minHeight: 44 },
        }}
      >
        {visibleTabs.map((t) => (
          <Tab key={t.path} label={t.label} />
        ))}
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdministrationLayout;
